<template>
  <b-modal
    ref="modal"
    hide-footer
    no-close-on-backdrop
    content-class="import-modal-content"
    dialog-class="import-modal-dialog"
    :title="labels.title"
  >
    <div class="groups-importer-wrapper">
      <b-form-file
        v-model="file"
        :browse-text="labels.form.file.button"
        :placeholder="labels.form.file.placeholder"
        no-drop
        accept=".xls, .xlsx"
        @change="readSheet"
      />

      <div class="sheet-selection mt-2" v-if="sheets.length > 0">
        <b-row>
          <b-col cols="12" xl="6">
            <b-form-group>
              <b-form-select @change="selectGroupsSheet">
                <option
                  v-for="(wsname, index) in sheets"
                  :key="`sheet-${index}`"
                  :value="wsname"
                >
                  {{ wsname }}
                </option>
              </b-form-select>
              <label class="custom-input-label">
                {{ labels.form.sheet.groups }}
              </label>
              <label class="mdi mdi-chevron-down select-icon"></label>
            </b-form-group>
          </b-col>
        </b-row>
        <div class="sheet-data-mapping" v-if="keys.length > 0">
          <hr />
          <b-row>
            <b-col cols="6" xl="6">
              <b-form-group>
                <b-form-select @input="selectMapping($event, 'group')">
                  <option
                    v-for="(key, keyIdx) in keys"
                    :key="`sheet-${keyIdx}`"
                    :value="key"
                  >
                    {{ key }}
                  </option>
                </b-form-select>
                <label class="custom-input-label">
                  {{ labels.form.sheet.group }}
                </label>
                <label class="mdi mdi-chevron-down select-icon"></label>
              </b-form-group>
            </b-col>
            <b-col cols="6" xl="6">
              <b-form-group>
                <b-form-select @input="selectMapping($event, 'parentGroup')">
                  <option
                    v-for="(key, keyIdx) in keys"
                    :key="`sheet-${keyIdx}`"
                    :value="key"
                  >
                    {{ key }}
                  </option>
                </b-form-select>
                <label class="custom-input-label">
                  {{ labels.form.sheet.parentGroup }}
                </label>
                <label class="mdi mdi-chevron-down select-icon"></label>
              </b-form-group>
            </b-col>
          </b-row>
          <hr />
          <div class="load-users-button text-center">
            <button
              v-if="groups.length === 0"
              type="button"
              class="btn btn-tertiary btn-lg"
              @click="loadGroups"
            >
              <i class="material-icons">
                file_upload
              </i>
              <span class="ml-2">
                {{ labels.form.loadGroups }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div class="sheet-data-found text-center" v-if="groups.length > 0">
        <b-row class="group-tree-select-wrapper" v-if="!loading">
          <b-col cols="12">
            <div class="group-tree-select-row">
              <treeselect
                ref="treeselect"
                name="group-tree-select"
                showCountOf="ALL_DESCENDANTS"
                :placeholder="
                  labels.form.foundGroups
                    | parse({
                      GROUPS: groups.length,
                      GROUPS_TREE: groupsTree.length,
                    })
                "
                :options="groupsTree"
                :clearable="clearable"
                :searchable="searchable"
                :open-on-click="openOnClick"
                :open-on-focus="openOnFocus"
                :close-on-select="closeOnSelect"
                :show-count="showCount"
                :always-open="alwaysOpen"
              ></treeselect>
            </div>
          </b-col>
        </b-row>
        <p v-if="loading">
          <i class="mdi mdi-spin mdi-loading pr-2"></i>
          <span>{{ loadingMessage }}</span>
        </p>
        <button
          type="button"
          class="btn btn-secondary"
          @click="createGroups"
          :disabled="loading"
        >
          <span class="ml-2">
            {{ labels.form.createGroups }}
          </span>
        </button>
        <b-alert
          show
          class="alert-secondary mt-4"
          v-if="loadingErrors.length > 0"
        >
          <p :key="`error-${index}`" v-for="(error, index) in loadingErrors">
            {{ error }}
          </p>
        </b-alert>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import Treeselect from '@riophae/vue-treeselect'
import XLSX from 'xlsx'

const KEYS_GROUPS = {
  group: null,
  parentGroup: null,
}

export default {
  components: {
    Treeselect,
  },

  data: function() {
    return {
      file: null,
      workbook: null,
      sheet: null,
      keys: [],
      keysGroups: KEYS_GROUPS,
      // group tree
      clearable: true,
      searchable: true,
      openOnClick: true,
      openOnFocus: true,
      closeOnSelect: true,
      showCount: true,
      alwaysOpen: true,
    }
  },

  computed: {
    ...mapGetters({
      resources: 'resources',
      groups: 'groups/groups',
      groupsTree: 'groups/groupsTree',
      loading: 'groups/loading',
      loadingMessage: 'groups/loadingMessage',
      loadingErrors: 'groups/loadingErrors',
    }),

    labels() {
      return this.resources.component.groupImporter
    },

    sheets() {
      const workbook = this.workbook || { SheetNames: [] }
      return workbook.SheetNames
    },
  },

  methods: {
    clearForm() {
      this.workbook = null
      this.sheet = null
      this.keys = []
      this.keysGroups = KEYS_GROUPS
      this.$store.dispatch('groups/clearImportForm')
    },

    show() {
      this.file = null
      this.clearForm()
      this.$refs.modal.show()
    },

    async createGroups() {
      const successSave = await this.$store.dispatch(
        'groups/createGroups',
        this.labels
      )
      if (successSave) {
        this.$emit('reloadGroups')
        this.$refs.modal.hide()
      }
    },

    loadGroups() {
      const { group = false, parentGroup = false } = this.keysGroups

      // get groups from sheet
      const groups = []
      this.sheet.forEach(line => {
        const g = {
          name: (String(line[group]) || '').trim(),
          parent: (String(line[parentGroup]) || '').trim(),
        }
        groups.push(g)
      })

      // create groups map
      const groupsMap = groups.reduce((map, group) => {
        const id = group.name
        group.parent = id === group.parent ? '' : group.parent
        if (map[id]) {
          map[id].parents.push(group.parent)
        } else {
          map[id] = {
            id,
            label: id,
            parents: [group.parent],
          }
        }
        return map
      }, {})

      // get root groups
      const rootGroups = []
      groups.forEach(group => {
        const g = groupsMap[group.name]
        const parents = g.parents.filter(parent => groupsMap[parent])

        if (parents.length === 0) {
          rootGroups.push(g)
        }

        parents.forEach(parentId => {
          if (!groupsMap[parentId].children) {
            groupsMap[parentId].children = []
          }

          groupsMap[parentId].children.push(g)
        })
      })

      this.$store.dispatch('groups/updateImport', {
        groups: Object.values(groupsMap),
        groupsTree: rootGroups,
      })
    },

    readSheet(e) {
      const files = e.target.files
      const fileReader = new FileReader()
      fileReader.onload = event => {
        this.clearForm()
        this.workbook = XLSX.read(event.target.result, { type: 'binary' })
      }
      fileReader.readAsBinaryString(files[0])
    },

    selectMapping(value, mapping) {
      this.keysGroups[mapping] = value
    },

    selectGroupsSheet(wsname) {
      const sheet = XLSX.utils.sheet_to_json(this.workbook.Sheets[wsname], {
        defval: null,
      })
      this.sheet = sheet
      this.keys = sheet && sheet.length > 0 ? Object.keys(sheet[0]) : []
    },
  },
}
</script>

<style scoped lang="scss">
@import '~@/styles/custom/colors';

.groups-importer-wrapper {
  .alert-secondary {
    color: map-get($theme-colors, 'white') !important;
    background-color: map-get($theme-colors, 'secondary') !important;
    font-size: 0.8em !important;
    position: relative !important;
    padding: 0.75rem 1.25rem;
    margin-bottom: 1rem;
    border: 1px solid transparent;
    border-radius: 0.25rem !important;

    .material-icons {
      margin-right: 10px;
      line-height: inherit !important;
    }
  }

  .group-tree-select-wrapper {
    flex-grow: 1;
    height: 300px;

    .group-tree-select-row {
      display: flex;
      flex-direction: column;

      /deep/ .vue-treeselect {
        font-size: 1.1rem;

        .vue-treeselect__menu {
          overflow-x: auto;
          height: 200px;
        }

        .vue-treeselect__placeholder {
          margin-top: 7px;
          color: map-get($theme-colors, 'primary') !important;
        }

        .vue-treeselect__control {
          height: 50px !important;

          .vue-treeselect__input {
            background-color: map-get($theme-colors, 'white') !important;
          }
        }

        .vue-treeselect__label-container {
          .vue-treeselect__label {
            overflow: inherit;
          }
        }

        .vue-treeselect__option--selected {
          background-color: map-get($theme-colors, 'gray08');
        }

        .vue-treeselect__single-value {
          margin-top: 7px;
        }
      }
    }
  }
}
</style>
